<template>
  <div class="formField fileField" :class="{ hasFocus, hasValue, isError, isDisabled, isInline }">
    <div class="fileField__InputWrapper">
      <FormLabel v-bind="formLabelProps">
        <slot name="tooltip" />
      </FormLabel>
      <div class="fileField__Wrapper">
        <div class="fileField__PrependText">
          <div class="fileField__Prepend" v-if="$slots['prepend']">
            <slot name="prepend" />
          </div>
          <span class="fileField__Text" v-text="text" />
        </div>
        <label
          class="fileField__Label"
          :for="`file-field-${uniqueID}`"
          :tabindex="!isDisabled ? 0 : -1"
          @keydown="handleKeyDown"
        >
          <span v-text="browseText" />
          <Icon name="upload"/>
        </label>
        <input
          :id="`file-field-${uniqueID}`"
          :name="name"
          type="file"
          ref="fileInput"
          :disabled="isDisabled"
          :required="isRequired"
          :multiple="multiple"
          :accept="accept"
          :placeholder="placeholder"
          tabindex="-1"
          @focus="setFocus(true)"
          @blur="setFocus(false)"
          @input="changeFieldValue"
        />
      </div>
      <FormError :errors="mergedErrors" className="fileField__Error" />
      <span class="fileField__Info form__Info" v-html="info" v-if="info" />
      <span class="fileField__Info form__Info" v-html="additionalInfo" v-if="additionalInfo" />
    </div>
    <div class="formField__Files" v-if="hasValue">
      <div class="formField__File" v-for="(file, i) in fieldValue">
        <span class="formField__FileName" v-text="file.name" />
        <button type="button" @click.prevent="deleteFile(i)">
          <span>X</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  const emit = defineEmits(['update:modelValue'])
  const props = defineProps({
    ...useFormFieldProps(),
    placeholder: {
      type: [String, Number]
    },
    multiple: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String
    },
    browseText: {
      type: String,
      default: 'Browse'
    },
    empty: {
      type: String,
      default: 'No items selected'
    },
    additionalInfo: {
      type: String,
      default: '',
    },
  })

  const {
    isError,
    isDisabled,
    isRequired,
    isInline,
    hasValue,
    fieldValue,
    mergedErrors,
    uniqueID,
    formLabelProps,
    hasFocus,
    setFocus,
    triggerValidate,
  } = useFormField(props, emit)

  defineExpose({
    triggerValidate,
  })

  const fileInput = ref(null)

  const text = computed(() => {
    if (!hasValue.value) return props.placeholder || props.empty
    return fieldValue.value.map(f => f.name).join(', ')
  })

  const changeFieldValue = e => {
    fieldValue.value = [...e.target.files]
  }

  const deleteFile = i => {
    fieldValue.value.splice(i, 1)
    if (fieldValue.value.length === 0) fileInput.value.value = ''
  }

  const handleKeyDown = e => {
    if ([0, 13, 32].includes(e.keyCode) && isDisabled.value === false) {
      e.preventDefault()
      e.target.parentNode.parentNode.querySelector('[type="file"]').click()
    }
  }
</script>

<style lang="scss">
  @import url('@/assets/styles/components/forms/_fileField.scss');
</style>
